/**
 * Em que empresa e em que seção uma organização aparece na árvore (spec
 * 2026-10-21-kb-raizes, R2 e R8). Só para quem vê tudo; a trilha é `kb.org.raiz`.
 * Muda o lugar na árvore, não quem lê: o acesso continua nos perfis das pastas.
 */
import { useEffect, useState } from 'react'
import { Button, Group, Modal, Radio, Select, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { setKbOrgLugar, type KbLugar, type KbRaiz, type KbSecao } from '@/api/kb'
import { mensagemDeErro } from './acesso'

export interface AlvoDoLugar {
  organizationId: number
  rotulo: string
  atual: KbLugar
}

interface Props {
  alvo: AlvoDoLugar | null
  raizes: KbRaiz[]
  onClose: () => void
}

export function DialogoDeLugar({ alvo, raizes, onClose }: Props) {
  const qc = useQueryClient()
  const [raizId, setRaizId] = useState<string | null>(null)
  const [secao, setSecao] = useState<KbSecao>('projetos')

  useEffect(() => {
    if (!alvo) return
    setRaizId(String(alvo.atual.raiz_id))
    setSecao(alvo.atual.secao)
  }, [alvo])

  const salvar = useMutation({
    mutationFn: () => setKbOrgLugar(alvo!.organizationId, { raiz_id: Number(raizId), secao }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-raizes'] })
      notifications.show({ color: 'teal', title: 'Lugar atualizado', message: alvo!.rotulo })
      onClose()
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Não foi possível salvar', message: mensagemDeErro(e, 'Falha ao salvar o lugar.') }),
  })

  const igual = alvo !== null && Number(raizId) === alvo.atual.raiz_id && secao === alvo.atual.secao

  return (
    <Modal opened={alvo !== null} onClose={onClose} title={<Text fw={700}>Lugar de {alvo?.rotulo} na árvore</Text>}>
      <Stack gap="sm">
        <Select
          label="Empresa"
          data={raizes.map((r) => ({ value: String(r.id), label: r.nome }))}
          value={raizId}
          onChange={setRaizId}
          allowDeselect={false}
        />
        <Radio.Group label="Seção" value={secao} onChange={(v) => setSecao(v as KbSecao)}>
          <Stack gap={6} mt={4}>
            <Radio value="topo" label="No topo da empresa (como a GOVERNANÇA)" />
            <Radio value="projetos" label="Dentro de Projetos (clientes)" />
          </Stack>
        </Radio.Group>
        <Text size="xs" c="dimmed">Muda só onde a organização aparece na árvore. Quem lê cada pasta continua decidido pelos perfis dela.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button loading={salvar.isPending} disabled={raizId === null || igual} onClick={() => salvar.mutate()}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
